import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  Container,
  Typography,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  IconButton,
  Button,
  Box,
  CircularProgress,
  TextField,
  InputAdornment,
  Skeleton,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  ExpandMore,
  Email,
  CreditCard as CardIcon,
  Search,
  FileDownload,
  Upload,
  CheckCircle,
  FactCheck,
  Send,
  Close as Clear,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { ClubWithCounts, Card, ClubConfigurationStatus, PaginatedCardsResponse } from '../../types';
import { isAdmin } from '../../utils/auth';
import RequireEmailVerification from '../../components/auth/RequireEmailVerification';
import { useTranslation } from 'react-i18next';
import CardsTable from '../../components/cards/CardsTable';

const CardsPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [clubs, setClubs] = useState<ClubWithCounts[]>([]);
  const [cardsData, setCardsData] = useState<{ [clubId: string]: Card[] }>({});
  const [totalCards, setTotalCards] = useState<{ [clubId: string]: number }>({});
  const [loadingClubs, setLoadingClubs] = useState(true);
  const [loadingCards, setLoadingCards] = useState<{ [clubId: string]: boolean }>({});
  const [expandedClub, setExpandedClub] = useState<string | false>(false);
  const [searchTerms, setSearchTerms] = useState<{ [clubId: string]: string }>({});
  const searchTimeoutRef = useRef<{ [clubId: string]: NodeJS.Timeout }>({});
  const [page, setPage] = useState<{ [clubId: string]: number }>({});
  const [rowsPerPage, setRowsPerPage] = useState<{ [clubId: string]: number }>({});
  const [downloading, setDownloading] = useState<{ [key: string]: boolean }>({});
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [consistencyResults, setConsistencyResults] = useState<any>(null);
  const [consistencyModalOpen, setConsistencyModalOpen] = useState(false);
  const [clubConfigurations, setClubConfigurations] = useState<Record<string, ClubConfigurationStatus>>({});

  // Filter states per club
  const [filters, setFilters] = useState<{ [clubId: string]: {
    filterGenerated: boolean | null;
    filterSent: boolean | null;
    filterPaid: boolean | null;
  } }>({});

  useEffect(() => {
    fetchClubs();
  }, []);

  const fetchClubs = async (preservePagination: boolean = false) => {
    try {
      setLoadingClubs(true);
      const response = await api.get<ClubWithCounts[]>('/clubs-with-counts');
      setClubs(response.data);

      // Initialize pagination for each club (only for new clubs or initial load)
      if (!preservePagination) {
        const initialPage: { [clubId: string]: number } = {};
        const initialRowsPerPage: { [clubId: string]: number } = {};
        response.data.forEach(club => {
          // Only set if not already set
          initialPage[club.record_id] = page[club.record_id] ?? 0;
          initialRowsPerPage[club.record_id] = rowsPerPage[club.record_id] ?? 25;
        });
        setPage(initialPage);
        setRowsPerPage(initialRowsPerPage);
      }
      
      // Fetch configuration status for each club
      const configPromises = response.data.map(club =>
        api.get<ClubConfigurationStatus>(`/clubs/${club.record_id}/configuration-status`)
          .then(res => ({ clubId: club.record_id, status: res.data }))
          .catch(() => ({ clubId: club.record_id, status: null }))
      );
      
      const configurations = await Promise.all(configPromises);
      const configMap: Record<string, ClubConfigurationStatus> = {};
      configurations.forEach(({ clubId, status }) => {
        if (status) {
          configMap[clubId] = status;
        }
      });
      setClubConfigurations(configMap);
    } catch (error) {
      console.error('Failed to fetch clubs:', error);
    } finally {
      setLoadingClubs(false);
    }
  };

  const fetchCardsForClub = async (
    clubId: string,
    options?: {
      pageNum?: number;
      pageSizeNum?: number;
      search?: string;
      overrideFilters?: typeof filters[string];
    }
  ) => {
    const pageNum = options?.pageNum ?? (page[clubId] ?? 0);
    const pageSizeNum = options?.pageSizeNum ?? (rowsPerPage[clubId] ?? 25);
    const search = options?.search ?? searchTerms[clubId] ?? '';
    const clubFilters = options?.overrideFilters ?? filters[clubId] ?? {};

    try {
      setLoadingCards(prev => ({ ...prev, [clubId]: true }));

      // Build query params for paginated endpoint (page is 1-indexed on backend)
      const params = new URLSearchParams();
      params.append('page', String(pageNum + 1)); // Convert 0-indexed to 1-indexed
      params.append('page_size', String(pageSizeNum));

      if (search.trim()) {
        params.append('search', search.trim());
      }

      if (clubFilters.filterGenerated !== null && clubFilters.filterGenerated !== undefined) {
        params.append('filter_generated', String(clubFilters.filterGenerated));
      }
      if (clubFilters.filterSent !== null && clubFilters.filterSent !== undefined) {
        params.append('filter_sent', String(clubFilters.filterSent));
      }
      if (clubFilters.filterPaid !== null && clubFilters.filterPaid !== undefined) {
        params.append('filter_paid', String(clubFilters.filterPaid));
      }

      const response = await api.get<PaginatedCardsResponse>(`/cards-pg/club/${clubId}?${params.toString()}`);
      setCardsData(prev => ({ ...prev, [clubId]: response.data.items }));
      setTotalCards(prev => ({ ...prev, [clubId]: response.data.total }));
    } catch (error: any) {
      console.error(`Failed to fetch cards for club ${clubId}:`, error);
      setCardsData(prev => ({ ...prev, [clubId]: [] }));
      setTotalCards(prev => ({ ...prev, [clubId]: 0 }));
    } finally {
      setLoadingCards(prev => ({ ...prev, [clubId]: false }));
    }
  };

  const handleFilterChange = async (clubId: string, filterType: 'filterGenerated' | 'filterSent' | 'filterPaid', value: boolean | null) => {
    const newFilters = {
      ...filters[clubId],
      [filterType]: value
    };
    setFilters(prev => ({
      ...prev,
      [clubId]: newFilters
    }));
    // Reset page to 0 and fetch with the new filters
    setPage(prev => ({ ...prev, [clubId]: 0 }));
    fetchCardsForClub(clubId, { pageNum: 0, overrideFilters: newFilters });
  };

  const handleAccordionChange = (clubId: string) => async (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpandedClub(isExpanded ? clubId : false);

    // Lazy load cards when accordion is expanded
    if (isExpanded && cardsData[clubId] === undefined) {
      await fetchCardsForClub(clubId);
    }
  };

  // Search pilots from backend with debounce
  const searchPilots = useCallback((clubId: string, searchTerm: string) => {
    // Clear any existing timeout for this club
    if (searchTimeoutRef.current[clubId]) {
      clearTimeout(searchTimeoutRef.current[clubId]);
    }

    // Debounce the search - wait 300ms before making the API call
    searchTimeoutRef.current[clubId] = setTimeout(() => {
      // Reset page to 0 when searching and fetch with search term
      setPage(prev => ({ ...prev, [clubId]: 0 }));
      fetchCardsForClub(clubId, { pageNum: 0, search: searchTerm });
    }, 300);
  }, []);

  // Handle search input change
  const handleSearchChange = (clubId: string, value: string) => {
    setSearchTerms(prev => ({ ...prev, [clubId]: value }));
    searchPilots(clubId, value);
  };

  // Clear search for a club
  const handleClearSearch = (clubId: string) => {
    setSearchTerms(prev => ({ ...prev, [clubId]: '' }));
    setPage(prev => ({ ...prev, [clubId]: 0 }));
    // Clear any pending timeout
    if (searchTimeoutRef.current[clubId]) {
      clearTimeout(searchTimeoutRef.current[clubId]);
    }
    // Fetch without search term
    fetchCardsForClub(clubId, { pageNum: 0, search: '' });
  };

  // Handle page change - fetch new page from server
  const handlePageChange = (clubId: string, newPage: number) => {
    setPage(prev => ({ ...prev, [clubId]: newPage }));
    fetchCardsForClub(clubId, { pageNum: newPage });
  };

  // Handle rows per page change - fetch with new page size
  const handleRowsPerPageChange = (clubId: string, newRowsPerPage: number) => {
    setRowsPerPage(prev => ({ ...prev, [clubId]: newRowsPerPage }));
    setPage(prev => ({ ...prev, [clubId]: 0 }));
    fetchCardsForClub(clubId, { pageNum: 0, pageSizeNum: newRowsPerPage });
  };

  // Create refresh function for CardsTable
  const createRefreshForClub = (clubId: string) => async () => {
    // Save scroll position before refresh
    const scrollPosition = window.scrollY;

    await fetchCardsForClub(clubId);
    await fetchClubs(true); // Also refresh club counts, but preserve pagination

    // Restore scroll position after refresh
    requestAnimationFrame(() => {
      window.scrollTo(0, scrollPosition);
    });
  };

  const handleGenerateAllCards = async (clubId: string) => {
    if (!window.confirm('Are you sure you want to generate all cards for this club? This may take a while.')) {
      return;
    }
    
    const key = `generate-all-${clubId}`;
    try {
      setDownloading({ ...downloading, [key]: true });
      await api.post(`/generate-membership-cards/${clubId}`);
      
      // Refresh the cards for this club
      await fetchCardsForClub(clubId);
      // Also refresh club counts (preserve pagination)
      await fetchClubs(true);

      setSuccess('All cards generated successfully!');
      setError(null);
    } catch (error: any) {
      console.error('Failed to generate all cards:', error);
      let errorMessage = 'Failed to generate all cards';
      
      if (error.response?.status === 500) {
        errorMessage = error.response?.data?.detail || 'Server error: The club may not be configured properly. Please check club settings.';
      } else if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      }
      
      setError(errorMessage);
      setSuccess(null);
    } finally {
      setDownloading({ ...downloading, [key]: false });
    }
  };

  const handleSendAllEmails = async (clubId: string) => {
    if (!window.confirm('Are you sure you want to send emails to all members with generated cards? This action cannot be undone.')) {
      return;
    }
    
    const key = `send-all-emails-${clubId}`;
    try {
      setDownloading({ ...downloading, [key]: true });
      const response = await api.post(`/send-emails-club/${clubId}`);
      
      // Refresh the cards to update email sent status
      await fetchCardsForClub(clubId);
      
      const message = response.data?.message || 'All emails sent successfully!';
      setSuccess(message);
      setError(null);
    } catch (error: any) {
      console.error('Failed to send all emails:', error);
      let errorMessage = 'Failed to send emails';
      
      if (error.response?.status === 404) {
        errorMessage = error.response?.data?.detail || 'No emails to send or club not found';
      } else if (error.response?.status === 500) {
        errorMessage = error.response?.data?.detail || 'Server error: Failed to send emails. Please check email configuration.';
      } else if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      }
      
      setError(errorMessage);
      setSuccess(null);
    } finally {
      setDownloading({ ...downloading, [key]: false });
    }
  };

  // Bulk clear functions removed - regenerate/resend actions now handle clearing automatically

  const handleConsistencyCheck = async (clubId: string) => {
    const key = `consistency-check-${clubId}`;
    try {
      setDownloading({ ...downloading, [key]: true });
      const response = await api.post(`/consistency-check/${clubId}`);
      
      // Store results and open modal
      const results = response.data;
      setConsistencyResults(results);
      setConsistencyModalOpen(true);
      
      if (results.status === 'consistent') {
        setSuccess('Consistency check passed! Database and Google Sheet are in sync.');
      }
      
    } catch (error: any) {
      console.error('Failed to perform consistency check:', error);
      let errorMessage = 'Failed to perform consistency check';
      
      if (error.response?.status === 404) {
        errorMessage = error.response?.data?.detail || 'Google Sheet URL not found for this club';
      } else if (error.response?.status === 403) {
        errorMessage = 'You do not have permission to perform this operation';
      } else if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      }
      
      setError(errorMessage);
      setSuccess(null);
    } finally {
      setDownloading({ ...downloading, [key]: false });
    }
  };

  if (loadingClubs) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <RequireEmailVerification>
      <Container maxWidth="xl" sx={{ px: { xs: 1, sm: 2, md: 3 } }}>
      <Box sx={{
        mb: { xs: 2, sm: 4 },
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between',
        alignItems: { xs: 'stretch', sm: 'center' },
        gap: 2
      }}>
        <Typography variant={isMobile ? 'h5' : 'h4'} sx={{ fontWeight: 600 }}>{t('cards.title')}</Typography>
        <Box sx={{
          display: 'flex',
          gap: 1,
          flexWrap: 'wrap',
          justifyContent: { xs: 'flex-start', sm: 'flex-end' },
        }}>
          <Button
            variant="outlined"
            size={isMobile ? 'small' : 'medium'}
            startIcon={!isSmallMobile && <Search />}
            onClick={() => navigate('/app/cards/search')}
            sx={{
              borderColor: '#3388ff',
              color: '#3388ff',
              fontSize: { xs: '0.75rem', sm: '0.875rem' },
              px: { xs: 1.5, sm: 2 },
              '&:hover': {
                borderColor: '#0066ff',
                backgroundColor: 'rgba(51, 136, 255, 0.04)',
              },
            }}
          >
            {isSmallMobile ? <Search fontSize="small" /> : (isMobile ? 'Search' : 'Search All Cards')}
          </Button>
          {isAdmin() && (
            <Button
              variant="outlined"
              size={isMobile ? 'small' : 'medium'}
              startIcon={!isSmallMobile && <Send />}
              onClick={() => navigate('/app/cards/send-email')}
              sx={{
                borderColor: '#ff9800',
                color: '#ff9800',
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                px: { xs: 1.5, sm: 2 },
                '&:hover': {
                  borderColor: '#f57c00',
                  backgroundColor: 'rgba(255, 152, 0, 0.04)',
                },
              }}
            >
              {isSmallMobile ? <Send fontSize="small" /> : (isMobile ? 'Email' : 'Send Email (Beta)')}
            </Button>
          )}
          {isAdmin() && (
            <Button
              variant="contained"
              size={isMobile ? 'small' : 'medium'}
              startIcon={!isSmallMobile && <Upload />}
              onClick={() => navigate('/app/cards/import')}
              sx={{
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                px: { xs: 1.5, sm: 2 },
              }}
            >
              {isSmallMobile ? <Upload fontSize="small" /> : (isMobile ? 'Import' : 'Import Cards')}
            </Button>
          )}
        </Box>
      </Box>

      {error && (
        <Alert 
          severity="error" 
          sx={{ mb: 3 }}
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}

      {success && (
        <Alert 
          severity="success" 
          sx={{ mb: 3 }}
          onClose={() => setSuccess(null)}
        >
          {success}
        </Alert>
      )}

      {clubs.map((club) => {
        const displayCards = cardsData[club.record_id] || [];
        const clubTotalCards = totalCards[club.record_id] ?? 0;
        const isLoading = loadingCards[club.record_id];
        const currentPage = page[club.record_id] || 0;
        const currentRowsPerPage = rowsPerPage[club.record_id] || 25;

        return (
          <Accordion
            key={club.record_id}
            expanded={expandedClub === club.record_id}
            onChange={handleAccordionChange(club.record_id)}
            sx={{ 
              mb: 2,
              backgroundColor: club.total_cards === 0 ? '#f5f5f5' : 'background.paper',
              border: '1px solid',
              borderColor: club.is_visitor_club ? '#ff00fb' : (club.total_cards === 0 ? 'grey.300' : 'grey.200'),
              borderWidth: club.is_visitor_club ? 2 : 1,
              '&:hover': {
                borderColor: club.is_visitor_club ? '#cc00c8' : 'grey.400',
              },
            }}
          >
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Box sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: { xs: 'flex-start', sm: 'center' },
                width: '100%',
                pr: 2,
                gap: { xs: 1, sm: 0 }
              }}>
                <Box sx={{ flexGrow: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
                    <Typography variant={isMobile ? 'subtitle1' : 'h6'} sx={{ fontWeight: 600 }}>
                      {club.club_name}
                    </Typography>
                    {club.is_visitor_club && (
                      <Chip
                        label={isMobile ? 'Visitor' : 'Visitor Club'}
                        size="small"
                        sx={{
                          backgroundColor: '#ff00fb',
                          color: 'white',
                          fontWeight: 600,
                          fontSize: '0.7rem',
                        }}
                      />
                    )}
                  </Box>
                  {!isMobile && (
                    <Typography variant="body2" color="text.secondary">
                      {club.description}
                    </Typography>
                  )}
                </Box>
                <Box sx={{ display: 'flex', gap: { xs: 0.5, sm: 2 }, flexWrap: 'wrap' }}>
                  {club.total_cards === 0 ? (
                    <Chip label="No Cards" color="default" variant="outlined" size={isMobile ? 'small' : 'medium'} />
                  ) : (
                    <>
                      <Chip
                        label={isMobile ? club.total_cards : `Total: ${club.total_cards}`}
                        color="primary"
                        size={isMobile ? 'small' : 'medium'}
                      />
                      <Chip
                        label={isMobile ? club.active_cards : `Active: ${club.active_cards}`}
                        color="success"
                        size={isMobile ? 'small' : 'medium'}
                      />
                    </>
                  )}
                </Box>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              {/* Search field and filters for this club */}
              {!isLoading && (
                <Box sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                  <TextField
                    size="small"
                    placeholder={`Search in ${club.club_name}...`}
                    value={searchTerms[club.record_id] || ''}
                    onChange={(e) => handleSearchChange(club.record_id, e.target.value)}
                    sx={{
                      width: { xs: '100%', sm: 300 }
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          {loadingCards[club.record_id] ? (
                            <CircularProgress size={20} />
                          ) : (
                            <Search />
                          )}
                        </InputAdornment>
                      ),
                      endAdornment: searchTerms[club.record_id] && (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => handleClearSearch(club.record_id)}
                            size="small"
                          >
                            <Clear />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                  {/* Filter chips */}
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    <Chip
                      label={t('cards.notGenerated')}
                      size="small"
                      color={filters[club.record_id]?.filterGenerated === false ? 'error' : 'default'}
                      variant={filters[club.record_id]?.filterGenerated === false ? 'filled' : 'outlined'}
                      onClick={() => handleFilterChange(
                        club.record_id,
                        'filterGenerated',
                        filters[club.record_id]?.filterGenerated === false ? null : false
                      )}
                    />
                    <Chip
                      label={t('cards.notSent')}
                      size="small"
                      color={filters[club.record_id]?.filterSent === false ? 'warning' : 'default'}
                      variant={filters[club.record_id]?.filterSent === false ? 'filled' : 'outlined'}
                      onClick={() => handleFilterChange(
                        club.record_id,
                        'filterSent',
                        filters[club.record_id]?.filterSent === false ? null : false
                      )}
                    />
                    <Chip
                      label={t('cards.notPaid')}
                      size="small"
                      color={filters[club.record_id]?.filterPaid === false ? 'info' : 'default'}
                      variant={filters[club.record_id]?.filterPaid === false ? 'filled' : 'outlined'}
                      onClick={() => handleFilterChange(
                        club.record_id,
                        'filterPaid',
                        filters[club.record_id]?.filterPaid === false ? null : false
                      )}
                    />
                  </Box>
                </Box>
              )}
              
              {/* Configuration warning */}
              {clubConfigurations[club.record_id] && !clubConfigurations[club.record_id].is_configured && (
                <Alert 
                  severity="warning" 
                  sx={{ mb: 2 }}
                  action={
                    <Button 
                      color="inherit" 
                      size="small"
                      onClick={() => navigate(`/app/admin/configure-club/${club.record_id}`)}
                    >
                      Configure Club
                    </Button>
                  }
                >
                  <strong>Club Configuration Required</strong><br />
                  This club needs to be configured before cards can be generated or managed.
                  Missing: {clubConfigurations[club.record_id].missing_fields.join(', ')}
                </Alert>
              )}
              
              {isLoading ? (
                <Box sx={{ p: 3 }}>
                  <Skeleton variant="rectangular" height={200} />
                </Box>
              ) : displayCards.length === 0 ? (
                <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
                  {searchTerms[club.record_id] ? t('cards.noCardsSearch') : t('cards.noCards')}
                </Typography>
              ) : (
                <>
                  <CardsTable
                    cards={displayCards}
                    onRefresh={createRefreshForClub(club.record_id)}
                    hideAlerts={true}
                    onError={setError}
                    onSuccess={setSuccess}
                    nationalFederationEmail={club.national_federation_email}
                    page={currentPage}
                    rowsPerPage={currentRowsPerPage}
                    totalCount={clubTotalCards}
                    onPageChange={(newPage) => handlePageChange(club.record_id, newPage)}
                    onRowsPerPageChange={(newRowsPerPage) => handleRowsPerPageChange(club.record_id, newRowsPerPage)}
                  />
                  <Box sx={{
                    mt: 2,
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    gap: { xs: 1, sm: 2 },
                    flexWrap: 'wrap'
                  }}>
                    {isAdmin() && (
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={downloading[`consistency-check-${club.record_id}`] ? <CircularProgress size={16} /> : <FactCheck />}
                        disabled={downloading[`consistency-check-${club.record_id}`] || (clubConfigurations[club.record_id] && !clubConfigurations[club.record_id].is_configured)}
                        onClick={() => handleConsistencyCheck(club.record_id)}
                        sx={{
                          borderColor: 'info.main',
                          color: 'info.main',
                          fontSize: { xs: '0.7rem', sm: '0.8125rem' },
                        }}
                      >
                        {downloading[`consistency-check-${club.record_id}`] ? t('cards.checking') : (isMobile ? 'Check' : t('cards.consistencyCheck'))}
                      </Button>
                    )}
                    <Button
                      size="small"
                      variant="contained"
                      color="primary"
                      startIcon={downloading[`generate-all-${club.record_id}`] ? <CircularProgress size={16} /> : <CardIcon />}
                      disabled={downloading[`generate-all-${club.record_id}`] || displayCards.length === 0 || (clubConfigurations[club.record_id] && !clubConfigurations[club.record_id].is_configured)}
                      onClick={() => handleGenerateAllCards(club.record_id)}
                      sx={{ fontSize: { xs: '0.7rem', sm: '0.8125rem' } }}
                    >
                      {downloading[`generate-all-${club.record_id}`] ? '...' : (isMobile ? 'Generate All' : t('cards.generateAllCards'))}
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={downloading[`cards-${club.record_id}`] ? <CircularProgress size={16} /> : <Email />}
                      disabled={downloading[`cards-${club.record_id}`] || (clubConfigurations[club.record_id] && !clubConfigurations[club.record_id].is_configured)}
                      onClick={async () => {
                        const key = `cards-${club.record_id}`;
                        try {
                          setDownloading({ ...downloading, [key]: true });
                          await api.post(`/download-cards/${club.record_id}`);
                          setSuccess('Success! A ZIP file with all cards will be sent to your email shortly.');
                          setError(null);
                        } catch (error: any) {
                          console.error('Failed to request cards:', error);
                          setSuccess(null);
                          if (error.response?.status === 404) {
                            const errorDetail = error.response?.data?.detail || '';
                            if (errorDetail.includes('not generated yet')) {
                              setError('Cards have not been generated yet! Please click "Generate Cards" first to create the PDF cards, then try downloading again.');
                            } else if (errorDetail.includes('No cards found')) {
                              setError('No cards found for this club. Please add members and generate their cards first.');
                            } else {
                              setError(errorDetail || 'Some cards are missing. Please generate all cards first.');
                            }
                          } else if (error.response?.status === 500) {
                            setError('Failed to send email. Please check your email configuration and try again.');
                          } else {
                            setError('Failed to request cards. Please try again.');
                          }
                        } finally {
                          setDownloading({ ...downloading, [key]: false });
                        }
                      }}
                      sx={{ fontSize: { xs: '0.7rem', sm: '0.8125rem' } }}
                    >
                      {downloading[`cards-${club.record_id}`] ? '...' : (isMobile ? 'Download ZIP' : t('cards.downloadCardsZip'))}
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={downloading[`excel-${club.record_id}`] ? <CircularProgress size={16} /> : <FileDownload />}
                      disabled={downloading[`excel-${club.record_id}`]}
                      onClick={async () => {
                        const key = `excel-${club.record_id}`;
                        try {
                          setDownloading({ ...downloading, [key]: true });
                          const response = await api.post(`/download-soci-xls/${club.record_id}`);
                          if (response.data.url) {
                            window.open(response.data.url, '_blank');
                            setSuccess('Excel file generated successfully!');
                            setError(null);
                          }
                        } catch (error: any) {
                          console.error('Failed to export to Excel:', error);
                          setSuccess(null);
                          if (error.response?.status === 404) {
                            setError('No cards found for this club. Please add members first.');
                          } else {
                            setError('Failed to export to Excel. Please try again.');
                          }
                        } finally {
                          setDownloading({ ...downloading, [key]: false });
                        }
                      }}
                      sx={{ fontSize: { xs: '0.7rem', sm: '0.8125rem' } }}
                    >
                      {downloading[`excel-${club.record_id}`] ? '...' : (isMobile ? 'Excel' : t('cards.exportToExcel'))}
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={downloading[`send-all-emails-${club.record_id}`] ? <CircularProgress size={16} /> : <Email />}
                      disabled={downloading[`send-all-emails-${club.record_id}`] || displayCards.length === 0}
                      onClick={() => handleSendAllEmails(club.record_id)}
                      sx={{ fontSize: { xs: '0.7rem', sm: '0.8125rem' } }}
                    >
                      {downloading[`send-all-emails-${club.record_id}`] ? '...' : (isMobile ? 'Send All' : t('cards.sendAllEmails'))}
                    </Button>
                    {/* Bulk clear buttons removed - use regenerate/resend for individual cards */}
                  </Box>
                </>
              )}
            </AccordionDetails>
          </Accordion>
        );
      })}
      </Container>

      {/* Consistency Check Results Modal */}
      <Dialog 
        open={consistencyModalOpen} 
        onClose={() => setConsistencyModalOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FactCheck />
            <Typography variant="h6">Consistency Check Results</Typography>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {consistencyResults && (
            <Box>
              <Box sx={{ mb: 3, p: 2, bgcolor: consistencyResults.status === 'consistent' ? 'success.light' : 'warning.light', borderRadius: 1 }}>
                <Typography variant="h6" sx={{ color: consistencyResults.status === 'consistent' ? 'success.dark' : 'warning.dark' }}>
                  Status: {consistencyResults.status === 'consistent' ? 'Consistent ✓' : 'Inconsistencies Found'}
                </Typography>
              </Box>

              {consistencyResults.missing_in_sheet && consistencyResults.missing_in_sheet.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                    Cards Missing in Google Sheet ({consistencyResults.missing_in_sheet.length})
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    These IDs exist in the database but not in the Google Sheet:
                  </Typography>
                  <Paper variant="outlined" sx={{ p: 2, maxHeight: 200, overflow: 'auto' }}>
                    <Typography variant="body2" component="div">
                      {consistencyResults.missing_in_sheet.join(', ')}
                    </Typography>
                  </Paper>
                </Box>
              )}

              {consistencyResults.new_in_sheet && consistencyResults.new_in_sheet.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                    New Entries in Google Sheet ({consistencyResults.new_in_sheet.length})
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    These IDs exist in the Google Sheet but not in the database:
                  </Typography>
                  <Paper variant="outlined" sx={{ p: 2, maxHeight: 200, overflow: 'auto' }}>
                    <Typography variant="body2" component="div">
                      {consistencyResults.new_in_sheet.join(', ')}
                    </Typography>
                  </Paper>
                </Box>
              )}

              {consistencyResults.mismatched_records && consistencyResults.mismatched_records.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                    Mismatched Records ({consistencyResults.mismatched_records.length})
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    These IDs have different data between the database and Google Sheet:
                  </Typography>
                  <Paper variant="outlined" sx={{ p: 2, maxHeight: 200, overflow: 'auto' }}>
                    <Typography variant="body2" component="div">
                      {consistencyResults.mismatched_records.join(', ')}
                    </Typography>
                  </Paper>
                </Box>
              )}

              {consistencyResults.status === 'consistent' && (
                <Box sx={{ textAlign: 'center', py: 2 }}>
                  <CheckCircle sx={{ fontSize: 48, color: 'success.main', mb: 2 }} />
                  <Typography variant="body1" color="text.secondary">
                    All records match between the database and Google Sheet!
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConsistencyModalOpen(false)} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </RequireEmailVerification>
  );
};

export default CardsPage;